import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { updateTemplate, selectTemplate } from 'export';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, switchMap, take, tap } from 'rxjs/operators';

/**
 * Prefetches ProseMirror schemas so mark/node editors have a fallback before a template is selected.
 */
@Injectable({
  providedIn: 'root',
})
export class ProsemirrorSchemaPrefetchService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);

  private load$?: Observable<unknown>;

  ensureLoaded(): void {
    if (!this.load$) {
      setTimeout(() => this.initializeStream(), 0);
    } else {
      this.subscribeToStream();
    }
  }

  private initializeStream(): void {
    if (this.load$) {
      this.subscribeToStream();
      return;
    }
    if (typeof console !== 'undefined' && console?.log) {
      console.log('[ProsemirrorSchemaPrefetch] Initializing stream');
    }
    if (!this.load$) {
      this.load$ = this.store.select(selectTemplate).pipe(
        take(1),
        switchMap((current) => {
          if (typeof console !== 'undefined' && console?.log) {
            console.log('[ProsemirrorSchemaPrefetch] Checking template store', {
              hasSchema: !!current?.prosemirrorSchemas,
            });
          }
          if (current?.prosemirrorSchemas) {
            return of(null);
          }
          return this.fetchSchemas().pipe(
            tap((schemas) => {
              if (typeof console !== 'undefined' && console?.log) {
                console.log('[ProsemirrorSchemaPrefetch] Prefetch response', {
                  hasSchemas: !!schemas,
                });
              }
              if (schemas) {
                this.store.dispatch(updateTemplate({ template: { prosemirrorSchemas: schemas } }));
              }
            }),
            catchError((error) => {
              console.warn('[ProsemirrorSchemaPrefetch] Failed to load fallback schemas', error);
              return of(null);
            }),
          );
        }),
        shareReplay(1),
      );
    }

    this.subscribeToStream();
  }

  private subscribeToStream(): void {
    this.load$?.subscribe({
      error: (error) => {
        console.warn('[ProsemirrorSchemaPrefetch] Subscription error', error);
      },
    });
  }

  private fetchSchemas(): Observable<unknown> {
    const endpoint = '/template/prosemirror-schemas.json';
    return this.http.get(endpoint).pipe(
      catchError((error) => {
        if (typeof console !== 'undefined' && console?.warn) {
          console.warn('[ProsemirrorSchemaPrefetch] Primary fetch failed', error);
        }
        if (typeof window !== 'undefined') {
          const { protocol, hostname } = window.location;
          const fallbackUrl = `${protocol}//${hostname}:3000${endpoint}`;
          if (typeof console !== 'undefined' && console?.log) {
            console.log('[ProsemirrorSchemaPrefetch] Retrying with fallback URL', fallbackUrl);
          }
          return this.http.get(fallbackUrl).pipe(
            catchError((fallbackError) => {
              return throwError(() => fallbackError);
            }),
          );
        }
        return throwError(() => error);
      }),
    );
  }
}
